import { useMemo, useState } from "react";
import { Link } from "wouter";
import { ChevronRight, Search, Store } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { BillingStatusBadge } from "@/components/sellers/BillingStatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Pagination } from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSellers } from "@/hooks/use-sellers";
import { regionMatches, regionOfSeller, useRegionResolver } from "@/hooks/use-regions";
import { usePagination } from "@/hooks/use-pagination";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection } from "@/hooks/use-firestore";
import { formatNumber, matches } from "@/lib/format";
import { formatRegion, type Region } from "@/lib/geo";
import type { BillingStatusValue, SellerBillingStatus } from "@/types";

type StatusFilter = "todos" | "ativos" | "inativos" | "abertos" | "bloqueados";

export default function Sellers() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("todos");
  const [state, setState] = useState("todos");
  const sellers = useSellers();
  const resolver = useRegionResolver();

  // A região sai da coordenada do cadastro, e não do texto digitado: "MG" e
  // "Minas Gerais" são o mesmo estado, "SRS" e "Santa Rita do Sapucaí" a mesma
  // cidade. Ver lib/geo.ts.
  const regions = useMemo(() => {
    const map = new Map<string, Region | null>();
    for (const seller of sellers.data ?? []) map.set(seller.id, regionOfSeller(seller, resolver.data));
    return map;
  }, [sellers.data, resolver.data]);

  const states = useMemo(() => {
    const set = new Set<string>();
    for (const region of regions.values()) if (region?.uf) set.add(region.uf);
    return [...set].sort();
  }, [regions]);

  // O status de cobrança vem de uma coleção separada, indexada pelo sellerId.
  // Uma consulta por vendedor seria uma por linha da tabela: a coleção inteira
  // é pequena (um documento por vendedor) e cabe numa leitura só.
  const billing = useQuery({
    queryKey: ["sellersBillingStatus"],
    queryFn: () => fetchCollection<SellerBillingStatus>("sellersBillingStatus"),
    staleTime: 60_000,
  });

  const billingBySeller = useMemo(() => {
    const map = new Map<string, BillingStatusValue>();
    for (const row of billing.data ?? []) {
      if (row.status) map.set(row.sellerId ?? row.id, row.status);
    }
    return map;
  }, [billing.data]);

  // Parceiro de teste: as faturas dele nascem ignoradas (ver TestPartnerCard).
  const testPartners = useMemo(() => {
    const set = new Set<string>();
    for (const row of billing.data ?? []) {
      if (row.isTestPartner === true) set.add(row.sellerId ?? row.id);
    }
    return set;
  }, [billing.data]);

  const rows = useMemo(() => {
    const all = sellers.data ?? [];
    return all.filter((seller) => {
      const region = regions.get(seller.id) ?? null;
      if (
        search &&
        !matches(seller.name, search) &&
        !matches(seller.email, search) &&
        !matches(seller.cnpj, search) &&
        !matches(seller.address?.city, search) &&
        !regionMatches(region, search)
      ) {
        return false;
      }
      if (state !== "todos" && region?.uf !== state) return false;
      switch (filter) {
        case "ativos":
          return seller.isActive === true;
        case "inativos":
          return seller.isActive !== true;
        case "abertos":
          return seller.isOpen === true;
        case "bloqueados":
          return seller.isActive === false && Boolean(seller.blockReason);
        default:
          return true;
      }
    });
  }, [sellers.data, search, filter, state, regions]);
  const pagination = usePagination(rows, `${search}|${filter}|${state}`);

  return (
    <>
      <PageHeader
        title="Vendedores"
        description={
          sellers.data
            ? `${formatNumber(sellers.data.length)} estabelecimentos cadastrados.`
            : "Estabelecimentos cadastrados na plataforma."
        }
      />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, e-mail, CNPJ ou cidade"
            className="pl-9"
            aria-label="Buscar vendedor"
          />
        </div>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="w-full sm:w-44" aria-label="Filtrar por estado">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os estados</SelectItem>
            {states.map((uf) => (
              <SelectItem key={uf} value={uf}>
                {uf}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter} onValueChange={(v) => setFilter(v as StatusFilter)}>
          <SelectTrigger className="w-full sm:w-48" aria-label="Filtrar por situação">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="ativos">Ativos</SelectItem>
            <SelectItem value="inativos">Inativos</SelectItem>
            <SelectItem value="abertos">Abertos agora</SelectItem>
            <SelectItem value="bloqueados">Bloqueados por fatura</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 sm:p-0">
          {sellers.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Store}
              title={
                search || filter !== "todos" || state !== "todos"
                  ? "Nenhum vendedor encontrado"
                  : "Nenhum vendedor cadastrado"
              }
              description={
                search || filter !== "todos" || state !== "todos"
                  ? "Ajuste a busca ou o filtro para ver outros resultados."
                  : "Os estabelecimentos aparecem aqui assim que concluem o cadastro no aplicativo do vendedor."
              }
            />
          ) : (
            <Table className="table-stacked">
              <TableHeader>
                <TableRow>
                  <TableHead>Estabelecimento</TableHead>
                  <TableHead className="hidden md:table-cell">Cidade</TableHead>
                  <TableHead className="hidden lg:table-cell">Nota</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="hidden md:table-cell">Cobrança</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.pageItems.map((seller) => (
                  <TableRow key={seller.id}>
                    <TableCell className="cell-title">
                      <Link
                        href={`/vendedores/${seller.id}`}
                        className="font-semibold text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {seller.name || "Sem nome"}
                      </Link>
                      <p className="truncate text-caption text-muted-foreground">
                        {seller.email || "Sem e-mail"}
                      </p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {regions.get(seller.id)
                        ? formatRegion(regions.get(seller.id))
                        : seller.address?.city
                          ? `${seller.address.city}${seller.address.state ? `, ${seller.address.state}` : ""}`
                          : "-"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell tabular-nums">
                      {seller.rating != null && seller.totalRatings
                        ? `${seller.rating.toFixed(1)} (${formatNumber(seller.totalRatings)})`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {seller.isActive ? (
                          <Badge variant="ok">Ativo</Badge>
                        ) : (
                          <Badge variant="error">Inativo</Badge>
                        )}
                        {seller.isOpen ? <Badge variant="default">Aberto</Badge> : null}
                        {seller.isTestSeller ? <Badge variant="muted">Teste</Badge> : null}
                        {testPartners.has(seller.id) ? (
                          <Badge variant="outline">Parceiro de teste</Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <BillingStatusBadge status={billingBySeller.get(seller.id)} />
                    </TableCell>
                    <TableCell className="cell-actions">
                      <Button asChild variant="ghost" size="sm">
                        <Link href={`/vendedores/${seller.id}`}>
                          Abrir <ChevronRight />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Pagination {...pagination.paginationProps} itemLabel="vendedores" />
        </CardContent>
      </Card>
    </>
  );
}
